<template>
  <div
    class="relative flex-shrink-0"
    :class="{ 'opacity-75': disabled }"
    :style="{ width: '100%' }"
  >
    <label v-if="label" class="mb-1 relative">
      <BlTextBody3 :text="label" />
      <span v-if="asterisk" class="text-error-400 text-xl absolute -top-1 ml-1">
        *
      </span>
    </label>
    <div class="relative" :class="{ 'cursor-not-allowed': disabled }">
      <div
        :data-id="id"
        class="relative min-h-10 w-full py-2 px-3 flex gap-2 flex-wrap border rounded-t cursor-text"
        :class="[
          focus || query ? 'border-light-purple' : 'border-grey-200',
          footer ? '' : 'rounded-b',
          disabled ? 'pointer-events-none bg-slate-300' : 'bg-pure-white'
        ]"
        @click="focus = !focus"
      >
        <div
          v-for="item in value"
          :key="item"
          class="h-7 px-2 flex items-center gap-1 rounded truncate transition-all duration-300"
          :class="{
            'bg-slate-300 border border-grey-100 hover:border-grey-400':
              type === 'tag',
            'bg-success-100 border border-success-100 hover:border-success-400':
              type === 'user',
            'border border-grey-100 hover:border-grey-400': type === 'branch'
          }"
          :style="{
            backgroundColor: getAttr(item, 'backgroundColor') || 'unset'
          }"
          :title="getAttr(item, 'label')"
        >
          <BlTextBody2
            :class="{
              'text-slate-800': type === 'tag',
              'text-success': type === 'user'
            }"
            class="w-full truncate"
            :style="{ color: getAttr(item, 'color') || 'unset' }"
            :text="getAttr(item, 'label')"
          />
          <div class="cursor-pointer" @click="onRemove(item)">
            <BlIcon
              icon="close"
              :color="type === 'user' ? 'success' : ''"
              class="h-4 w-4"
            />
          </div>
        </div>
        <input
          ref="search_box"
          type="text"
          class="h-8 min-w-64 text-sm font-normal focus:outline-none"
          v-model="query"
          :placeholder="value.length ? '' : placeholder"
          :disabled="disabled"
        />
      </div>

      <div
        :data-id="id"
        v-if="!disabled && (focus || query)"
        class="absolute z-50 max-h-72 w-full py-2 px-2 flex flex-col gap-2 flex-shrink-0 bg-pure-white rounded border border-grey-200 shadow-md"
        :class="{
          'mt-2': drop === 'down',
          'mb-2 bottom-full': drop === 'up'
        }"
      >
        <div
          v-if="filteredOptions.length === 0"
          :data-id="id"
          class="py-2 px-2 flex items-center justify-center"
        >
          <div v-if="fetchLoading" :data-id="id" class="blum-loader-sm"></div>
          <BlTextBody2
            v-else-if="query"
            :data-id="id"
            :text="`'${query}' not found`"
          />
          <BlTextBody2 v-else :data-id="id" :text="emptyOptions" />
        </div>
        <div :data-id="id" v-else class="flex flex-col overflow-y-auto">
          <div
            v-for="item in filteredOptions"
            :key="item.value"
            :data-id="id"
            class="py-2 px-2 flex items-center gap-2 flex-shrink-0 truncate rounded cursor-pointer transition-all duration-300 hover:bg-hover-state"
            @click="onAdd(item.value)"
          >
            <div
              :data-id="id"
              class="h-7 px-2 flex items-center rounded truncate"
              :class="{
                'bg-slate-300 border border-slate-400': type === 'tag',
                'bg-success-100': type === 'user'
              }"
              :style="{ backgroundColor: item.backgroundColor || 'unset' }"
              :title="item.label"
            >
              <BlTextBody2
                :data-id="id"
                :text="item.label"
                class="truncate"
                :class="{
                  'text-slate-800': type === 'tag',
                  'text-success': type === 'user'
                }"
                :style="{ color: item.color || 'unset' }"
              />
            </div>
          </div>
          <BlInputButton
            v-if="isShowMoreOption"
            :dataId="id"
            class="mt-2 hover:bg-hover-state"
            type="plain"
            label="See more ..."
            dense
            :loading="fetchLoading"
            @clicked="onMoreOption"
          />
        </div>
      </div>
    </div>
    <div v-if="footer">
      <slot />
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import debounce from "lodash/debounce";

import BlIcon from "./BlIcon.vue";
import BlTextBody2 from "./BlTextBody2.vue";
import BlTextBody3 from "./BlTextBody3.vue";

export default {
  name: "BlSearchBox",
  components: { BlIcon, BlTextBody3, BlTextBody2 },
  props: {
    dataId: { type: String, default: "" },
    disabled: { type: Boolean },
    type: { type: String, default: "tag" }, // tag, user, branch
    label: { type: String },
    asterisk: { type: Boolean },
    drop: { type: String, default: "down" },
    placeholder: { type: String },
    value: { default: () => [] },
    options: { default: () => [] }, // { value, label }[]
    emptyOptions: { type: String, default: "Option not found" },
    fetchProperty: {
      type: Object,
      default: () => ({
        url: "",
        dataKey: "",
        countKey: "total_count",
        optionKeys: { value: "value", label: "label" }
      })
    },
    footer: { type: Boolean, default: false },
    // @change(value)
    // deprecated
    searching: { type: Boolean, default: false }
    // @search(query)
  },
  data() {
    return {
      input: [],
      stateOptions: [],
      focus: false,
      query: "",
      fetchLoading: false,
      isShowMoreOption: false,
      fetchPage: 1
    };
  },
  computed: {
    id() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      if (this.dataId) return `${this.dataId}-bl-search-box-${combination}`;
      return `bl-search-box-${combination}`;
    },
    filteredOptions() {
      let options = [...this.stateOptions].filter(
        i => !this.input.includes(i.value)
      );
      // .sort((a, b) => a.label.localeCompare(b.label));
      if (!this.query) return options;
      if (options.length > 1) {
        options = options.filter(i => i.value !== -1);
      }
      if (!this.query) return options;
      return options.filter(i => {
        const lowerKey = this.query.toLowerCase();
        const lowerLabel = i.label.toLowerCase();
        return lowerLabel.includes(lowerKey);
      });
    }
  },
  watch: {
    focus(val) {
      if (val) {
        if (this.fetchProperty.url) {
          this.onFetchOptions();
        } else {
          this.fetchLoading = true;
          this.onSearch();
        }
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
          this.$refs.search_box.focus();
        }, 10);
      }
    },
    query(val) {
      this.fetchLoading = true;
      this.onSearch(val);
    },
    value: {
      immediate: true,
      handler(val) {
        this.input = val;
      }
    },
    options: {
      deep: true,
      immediate: true,
      handler(val) {
        const lastArr = [...this.stateOptions, ...val];
        const uniqueArr = this.removeDuplicates(lastArr, "value");
        this.stateOptions = uniqueArr;
      }
    },
    //
    searching(val) {
      this.fetchLoading = val;
    }
  },
  mounted() {},
  methods: {
    ...mapActions(["actionGet"]),
    removeDuplicates(arr, key) {
      const seen = new Set();
      return arr.filter(item => {
        const val = item[key];
        if (seen.has(val)) {
          return false;
        }
        seen.add(val);
        return true;
      });
    },
    async onFetchOptions() {
      const url = this.fetchProperty.url;
      const query = this.query;
      const page = this.fetchPage;
      const dataKey = this.fetchProperty.dataKey;
      const countKey = this.fetchProperty.countKey;
      const keys = this.fetchProperty.optionKeys;
      const options = [...this.stateOptions];

      const params = { url: `${url}${query}` };
      params.url += `&per_page=${50}`;
      if (query) params.url += `&page=${1}`;
      else params.url += `&page=${page}`;

      this.fetchLoading = true;
      const { success, data } = await this.actionGet(params);
      this.fetchLoading = false;
      if (!success) return;
      if (!dataKey) return;

      const incomingArr = data[dataKey].map(i => {
        const opt = {
          value: i[keys.value],
          label: i[keys.label]
        };
        if (keys.color) opt.color = i[keys.color];
        if (keys.backgroundColor) opt.backgroundColor = i[keys.backgroundColor];
        return opt;
      });

      const lastArr = [...options, ...incomingArr];
      const uniqueArr = this.removeDuplicates(lastArr, "value");

      this.isShowMoreOption = true;
      if (uniqueArr.length >= data[countKey]) {
        this.isShowMoreOption = false;
      }

      this.stateOptions = uniqueArr;
    },
    onMoreOption() {
      this.fetchPage += 1;
      this.onFetchOptions();
    },
    //
    getAttr(val, key) {
      const found = this.stateOptions.find(i => i.value === val);
      return found ? found[key] || "" : "";
    },
    onSearch: debounce(function(e = "") {
      if (this.fetchProperty.url && e !== "") {
        this.onFetchOptions();
      } else {
        this.fetchLoading = false;
      }
      this.$emit("search", e);
    }, 1000),
    onAdd(item) {
      if (!this.input.find(i => i === item)) {
        const added = [...this.input, item];
        this.input = added;
        this.query = "";
        this.$emit("change", added);
      }
    },
    onRemove(item) {
      if (this.input.find(i => i === item)) {
        const removed = this.input.filter(i => i !== item);
        this.input = removed;
        this.$emit("change", removed);
      }
    },
    //
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.id) close = false;
        if (id.includes(this.id)) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.focus = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/BlSearchBox.vue